/**
 * Manages the W3C Screen Wake Lock API to prevent mobile devices from sleeping or locking
 * their screens during active field monitoring.
 *
 * Why: Mobile operating systems (Android, iOS Safari) automatically put un-touched screens
 * into sleep mode within 30-60 seconds. In a moving vehicle or handheld survey, screen timeouts
 * interrupt visual map navigation and can throttle background Bluetooth Low Energy (BLE) packet
 * reception. The WakeLockManager provides resilient lock acquisition, automatically re-locks
 * when returning to the tab, and gracefully degrades when unsupported.
 */
export class WakeLockManager {
  private desiredActive = false;
  private sentinel: WakeLockSentinel | null = null;
  private fallbackVideo: HTMLVideoElement | null = null;
  private isFallbackActiveState = false;
  private listeners: Array<(active: boolean) => void> = [];
  private boundVisibilityHandler: (() => void) | null = null;

  constructor() {
    this.setupVisibilityListener();
  }

  /**
   * Checks whether the current browser runtime supports the Screen Wake Lock API.
   *
   * Why: Safari prior to iOS 16.4 and non-secure HTTP contexts lack navigator.wakeLock support;
   * detecting capability allows UI controls to disable themselves or display informative tooltips.
   *
   * @returns True if navigator.wakeLock is available
   */
  isSupported(): boolean {
    return typeof navigator !== 'undefined' && 'wakeLock' in navigator;
  }

  /**
   * Checks whether the browser runtime supports the iOS media element fallback for screen lock.
   *
   * Why: Enables display sleep prevention on iOS Safari and browsers where navigator.wakeLock is unavailable.
   *
   * @returns True if DOM video fallback can be created
   */
  isFallbackSupported(): boolean {
    return typeof document !== 'undefined' && typeof document.createElement === 'function';
  }

  /**
   * Returns whether a screen wake lock is currently held and active at the operating system level
   * via either the native Screen Wake Lock API or the iOS media fallback.
   *
   * Why: Enables status indicators and toggle controls to reflect live OS-level lock state.
   *
   * @returns True if screen lock sentinel or fallback video loop is active
   */
  isActive(): boolean {
    return (this.sentinel !== null && !this.sentinel.released) || this.isFallbackActiveState;
  }

  /**
   * Returns whether the iOS Safari media fallback loop is currently holding the screen awake.
   *
   * @returns True if media fallback is active
   */
  isFallbackActive(): boolean {
    return this.isFallbackActiveState;
  }

  /**
   * Returns whether the operator has requested the screen to stay awake.
   *
   * Why: Distinguishes between intentional user deactivation and transient OS-level releases
   * caused by app-switching or tab minimization.
   *
   * @returns True if user has enabled wake lock mode
   */
  isDesiredActive(): boolean {
    return this.desiredActive;
  }

  /**
   * Requests a screen wake lock from the platform.
   *
   * Why: Prevents display timeout and keeps tactical map and packet ingestion live.
   *
   * @returns Promise resolving to true if lock was acquired, false if rejected or unsupported
   */
  async enable(): Promise<boolean> {
    this.desiredActive = true;
    return this.acquireLock();
  }

  /**
   * Releases the active screen wake lock and cancels automatic re-acquisition.
   *
   * Why: Conserves smartphone battery when the operator completes their session
   * or explicitly chooses to allow standard OS sleep timeouts.
   */
  async disable(): Promise<void> {
    this.desiredActive = false;
    await this.releaseLock();
  }

  /**
   * Toggles the desired wake lock state between enabled and disabled.
   *
   * Why: Convenient handler for single-touch UI checkboxes and buttons.
   *
   * @returns Promise resolving to new active state
   */
  async toggle(): Promise<boolean> {
    if (this.desiredActive) {
      await this.disable();
      return false;
    } else {
      return this.enable();
    }
  }

  /**
   * Registers a callback listener notified whenever the wake lock state changes.
   *
   * Why: Decouples UI indicators (such as drawer checkboxes or status icons) from
   * internal OS release events and tab visibility transitions.
   *
   * @param listener - Callback receiving boolean active state
   * @returns Cleanup function to unregister the listener
   */
  onStateChange(listener: (active: boolean) => void): () => void {
    this.listeners.push(listener);
    return () => {
      this.listeners = this.listeners.filter((cb) => cb !== listener);
    };
  }

  /**
   * Subscribes to document visibility changes to restore locks after backgrounding.
   *
   * Why: Mobile browsers immediately release all WakeLocks when switching apps or minimizing tabs;
   * listening to visibilitychange re-acquires the lock the instant the operator returns to NodeRadar.
   */
  private setupVisibilityListener(): void {
    if (typeof document === 'undefined') return;

    this.boundVisibilityHandler = async () => {
      if (document.visibilityState === 'visible' && this.desiredActive) {
        await this.acquireLock();
      }
    };

    document.addEventListener('visibilitychange', this.boundVisibilityHandler);
  }

  /**
   * Requests the actual screen lock from the platform navigator or initiates the iOS media fallback.
   *
   * Why: Encapsulates asynchronous platform request and provides seamless fallback to silent video playback
   * on iOS Safari where Screen Wake Lock API is either unsupported or blocked by low power mode.
   */
  private async acquireLock(): Promise<boolean> {
    // 1. If native W3C Screen Wake Lock API is supported, attempt it first
    if (this.isSupported()) {
      // Already holding active native lock
      if (this.sentinel && !this.sentinel.released) {
        this.notifyListeners(true);
        return true;
      }

      try {
        this.sentinel = await navigator.wakeLock.request('screen');

        this.sentinel.addEventListener('release', () => {
          // Sentinel released by OS (tab hidden, low battery, etc.)
          this.sentinel = null;
          this.notifyListeners(false);
        });

        // Release fallback video if it was previously active
        this.stopFallbackVideo();

        this.notifyListeners(true);
        return true;
      } catch (err) {
        console.warn('[WakeLockManager] Screen Wake Lock request rejected, attempting iOS fallback:', err);
        this.sentinel = null;
      }
    }

    // 2. Engage iOS Safari / browser media element fallback
    if (this.isFallbackSupported()) {
      try {
        const success = await this.startFallbackVideo();
        if (success) {
          this.notifyListeners(true);
          return true;
        }
      } catch (err) {
        console.warn('[WakeLockManager] Fallback video activation failed:', err);
      }
    }

    this.notifyListeners(false);
    return false;
  }

  /**
   * Releases current sentinel or fallback video without altering desired state.
   *
   * Why: Ensures clean disposal of browser lock handles without throwing on already released locks.
   */
  private async releaseLock(): Promise<void> {
    if (this.sentinel) {
      try {
        await this.sentinel.release();
      } catch (err) {
        console.warn('[WakeLockManager] Error releasing screen wake lock:', err);
      } finally {
        this.sentinel = null;
      }
    }
    this.stopFallbackVideo();
    this.notifyListeners(false);
  }

  private async startFallbackVideo(): Promise<boolean> {
    if (typeof document === 'undefined') return false;

    if (!this.fallbackVideo) {
      const video = document.createElement('video');
      video.setAttribute('playsinline', '');
      video.setAttribute('muted', '');
      video.setAttribute('loop', '');
      video.muted = true;
      video.loop = true;
      video.style.position = 'absolute';
      video.style.left = '-9999px';
      video.style.width = '1px';
      video.style.height = '1px';
      video.style.opacity = '0.01';
      video.style.pointerEvents = 'none';
      video.src = 'data:video/mp4;base64,AAAAHGZ0eXBtcDQyAAAAAG1wNDJpc29tYXZjMQAAAAhmcmVlAAAAEG1kYXQAAAAAAAAAAQAAABhkbW9vdgAAAGxtdmhkAAAAAM21z0jNtc9IAABwgAABvAAAAVQAAAABAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAABAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAgAAACpdHJhawAAAFx0a2hkAAAAAw21z0jNtc9IAAAAAQAAAAAAAVQAAAAAAAAAAAAAAAAAAAAAAAEAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAgAAAAAAAAAAAAAA';
      document.body.appendChild(video);
      this.fallbackVideo = video;
    }

    try {
      const playPromise = this.fallbackVideo.play ? this.fallbackVideo.play() : Promise.resolve();
      if (playPromise) await playPromise;
      this.isFallbackActiveState = true;
      return true;
    } catch {
      this.isFallbackActiveState = false;
      return false;
    }
  }

  private stopFallbackVideo(): void {
    if (this.fallbackVideo) {
      try {
        if (typeof this.fallbackVideo.pause === 'function') {
          this.fallbackVideo.pause();
        }
      } catch {}
      if (this.fallbackVideo.parentElement) {
        this.fallbackVideo.parentElement.removeChild(this.fallbackVideo);
      } else if (typeof document !== 'undefined' && document.body && typeof document.body.removeChild === 'function') {
        try {
          document.body.removeChild(this.fallbackVideo);
        } catch {}
      }
      this.fallbackVideo = null;
    }
    this.isFallbackActiveState = false;
  }

  /**
   * Broadcasts the current active status to all registered listeners.
   *
   * Why: Keeps all synchronized UI components consistent with the true OS state.
   */
  private notifyListeners(active: boolean): void {
    this.listeners.forEach((listener) => {
      try {
        listener(active);
      } catch (err) {
        console.error('[WakeLockManager] Error in state change listener:', err);
      }
    });
  }

  /**
   * Disposes visibility listeners and releases any held lock.
   *
   * Why: Prevents memory leaks and lingering lock handles during testing or teardown.
   */
  dispose(): void {
    if (typeof document !== 'undefined' && this.boundVisibilityHandler) {
      document.removeEventListener('visibilitychange', this.boundVisibilityHandler);
      this.boundVisibilityHandler = null;
    }
    this.disable();
    this.listeners = [];
  }
}
